/**
 * Floating pill in the Flow Designer that reveals a count of pending
 * proposals and opens the Proposals tray popover.
 */

import { motion } from 'framer-motion';
import { GitPullRequest, CaretDown } from '@phosphor-icons/react';
import { Flex, Text, TagBadge, badgeVariantToTagColor } from '@chatsdet/ui';
import { scoreBand, type ReviewScoreBand } from '@chatsdet/types';
import type { ProposalSource, ProposalSummary } from './types';

const REVIEW_BAND_COLOR: Record<ReviewScoreBand, string> = {
  good: '#4DA6FF',
  'needs-work': '#FFBD0F',
  poor: '#E5484D',
};

interface ProposalsPillProps {
  count: number;
  isLoading: boolean;
  isOpen: boolean;
  onClick: () => void;
}

export function ProposalsPill({
  count,
  isLoading,
  isOpen,
  onClick,
}: ProposalsPillProps) {
  if (isLoading && count === 0) return null;
  if (!isLoading && count === 0) return null;

  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      whileHover={{ y: -1 }}
      whileTap={{ scale: 0.98 }}
      className={[
        'group inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium shadow-sm transition-colors',
        'border-amber-300/70 bg-amber-50 text-amber-900 hover:bg-amber-100',
        'dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-900/50',
        isOpen ? 'ring-2 ring-amber-400/50 dark:ring-amber-500/50' : '',
      ].join(' ')}
      aria-expanded={isOpen}
    >
      <GitPullRequest size={14} weight="bold" className="text-amber-600 dark:text-amber-400" />
      <span>
        {count} {count === 1 ? 'proposal' : 'proposals'}
      </span>
      <TagBadge name="Review" color="yellow" hideDot className="!py-0 !px-1.5 text-[10px]" />
      <CaretDown
        size={12}
        weight="bold"
        className={[
          'transition-transform',
          isOpen ? 'rotate-180' : '',
        ].join(' ')}
      />
    </motion.button>
  );
}

interface ProposalsTrayProps {
  proposals: ProposalSummary[];
  onReview: (proposalId: string) => void;
}

const SOURCE_LABEL: Record<ProposalSource, string> = {
  agent: 'Agent',
  user: 'Teammate',
  import: 'Import',
  api: 'API',
};

const SOURCE_TONE: Record<ProposalSource, 'default' | 'secondary'> = {
  agent: 'default',
  user: 'secondary',
  import: 'secondary',
  api: 'secondary',
};

function formatRelative(iso: string): string {
  const delta = Date.now() - new Date(iso).getTime();
  const s = Math.round(delta / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export function ProposalsTray({ proposals, onReview }: ProposalsTrayProps) {
  return (
    <div className="space-y-3 p-1">
      <div className="px-2 pt-1">
        <Text weight="semibold" className="text-sm">
          Pending proposals
        </Text>
        <Text size="xs" className="text-muted-foreground">
          Review suggested changes to this flow.
        </Text>
      </div>
      <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
        {proposals.map((p, i) => (
          <motion.button
            key={p.id}
            type="button"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15, delay: i * 0.03 }}
            onClick={() => onReview(p.id)}
            className="w-full rounded-md border border-border/60 bg-card px-3 py-2.5 text-left transition-colors hover:border-amber-300 hover:bg-amber-50/60 dark:hover:bg-amber-950/30"
          >
            <Flex align="start" justify="between" gap="sm">
              <div className="min-w-0 flex-1 space-y-1">
                <Text weight="medium" className="text-sm truncate">
                  {p.title}
                </Text>
                {p.description && (
                  <Text
                    size="xs"
                    className="text-muted-foreground line-clamp-2"
                  >
                    {p.description}
                  </Text>
                )}
                <Flex align="center" gap="xs" className="pt-0.5">
                  <TagBadge name={SOURCE_LABEL[p.source]} color={badgeVariantToTagColor(SOURCE_TONE[p.source])} hideDot className="text-[10px]" />
                  {p.reviewScorecard && (
                    <span
                      className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold text-white"
                      style={{ backgroundColor: REVIEW_BAND_COLOR[scoreBand(p.reviewScorecard.overall)] }}
                      title="AI review score"
                    >
                      {p.reviewScorecard.overall}
                    </span>
                  )}
                  {(() => {
                    const forkName = p.sourceMetadata?.forkWorkflowName;
                    return typeof forkName === 'string' ? (
                      <Text size="xs" className="text-muted-foreground truncate">
                        from fork: {forkName}
                      </Text>
                    ) : null;
                  })()}
                  {p.commentCount > 0 && (
                    <Text size="xs" className="text-muted-foreground">
                      {p.commentCount} {p.commentCount === 1 ? 'comment' : 'comments'}
                    </Text>
                  )}
                  <Text size="xs" className="text-muted-foreground">
                    {formatRelative(p.createdAt)}
                  </Text>
                </Flex>
              </div>
              <Text
                size="xs"
                className="shrink-0 font-medium text-amber-700 dark:text-amber-400"
              >
                Review →
              </Text>
            </Flex>
          </motion.button>
        ))}
      </div>
    </div>
  );
}
